"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import PublicHeader from "@/components/shared/PublicHeader";
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Activity,
  Database,
  Shield,
  Bot,
  HardDrive,
  Globe,
  Zap,
  Clock,
} from "lucide-react";

interface ServiceStatus {
  name: string;
  status: "operational" | "degraded" | "down";
  latency: number | null;
  message?: string;
}

interface StatusData {
  status: "operational" | "degraded" | "major_outage";
  timestamp: string;
  services: ServiceStatus[];
}

const SERVICE_ICONS: Record<string, React.ElementType> = {
  API: Zap,
  Database: Database,
  Authentication: Shield,
  "AI Chat Engine": Bot,
  "File Storage": HardDrive,
  Website: Globe,
};

const STATUS_CONFIG = {
  operational: {
    label: "Operational",
    color: "text-green-600",
    bg: "bg-green-100 dark:bg-green-900/30",
    dot: "bg-green-500",
    icon: CheckCircle,
  },
  degraded: {
    label: "Degraded",
    color: "text-yellow-600",
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    dot: "bg-yellow-500",
    icon: AlertTriangle,
  },
  down: {
    label: "Down",
    color: "text-red-600",
    bg: "bg-red-100 dark:bg-red-900/30",
    dot: "bg-red-500",
    icon: XCircle,
  },
  major_outage: {
    label: "Major Outage",
    color: "text-red-600",
    bg: "bg-red-100 dark:bg-red-900/30",
    dot: "bg-red-500",
    icon: XCircle,
  },
};

export default function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const loadStatus = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await fetch("/api/status", { cache: "no-store" });
      const result = await res.json();
      setData(result);
      setLastChecked(new Date());
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => loadStatus(true), 60000);
    return () => clearInterval(interval);
  }, []);

  const overallConfig = data
    ? STATUS_CONFIG[data.status] || STATUS_CONFIG.operational
    : STATUS_CONFIG.operational;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <PublicHeader variant="minimal" links={[{href: "/changelog", label: "Changelog"}]} sticky ctaOverride={{href: "/login", label: "Dashboard"}} maxWidth="max-w-3xl" />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Overall Status */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Activity className="w-5 h-5 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">System Status</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Checking services...</span>
            </div>
          ) : data ? (
            <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full ${overallConfig.bg} mt-2`}>
              <span className={`w-2.5 h-2.5 rounded-full ${overallConfig.dot} animate-pulse`} />
              <span className={`text-sm font-semibold ${overallConfig.color}`}>
                {data.status === "operational"
                  ? "All Systems Operational"
                  : data.status === "degraded"
                  ? "Some Systems Degraded"
                  : "Service Disruption Detected"}
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-100 dark:bg-red-900/30 mt-2">
              <XCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-red-600">Unable to reach status API</span>
            </div>
          )}

          {/* Last checked + Refresh */}
          <div className="flex items-center justify-center gap-3 mt-3">
            {lastChecked && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last checked: {lastChecked.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => loadStatus(true)}
              disabled={refreshing}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 transition"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Checking..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Service Cards */}
        {data && (
          <div className="space-y-3 mb-12">
            {data.services.map((service) => {
              const config = STATUS_CONFIG[service.status];
              const Icon = SERVICE_ICONS[service.name] || Activity;
              return (
                <div
                  key={service.name}
                  className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-5 py-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                      <Icon className="w-4.5 h-4.5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{service.name}</p>
                      {service.message && (
                        <p className="text-xs text-gray-400 mt-0.5">{service.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {service.latency !== null && (
                      <span className="text-xs text-gray-400 font-mono">{service.latency}ms</span>
                    )}
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.bg}`}>
                      <span className={`w-2 h-2 rounded-full ${config.dot}`} />
                      <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Uptime Commitment */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Our Uptime Commitment</h2>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">99.9%</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Target SLA</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">24/7</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Monitoring</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">&lt;200ms</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">API Latency Goal</p>
            </div>
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
              <strong>Honest note:</strong> We are a growing platform and do not yet have 90 days of public uptime history.
              We are committed to transparency and will publish real uptime metrics once we have sufficient historical data.
              This page shows live health checks — not cached or simulated data.
            </p>
          </div>
        </div>

        {/* Incident History */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-4">Recent Incidents</h2>
          <div className="text-center py-6">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">No incidents reported in the last 30 days.</p>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            Experiencing issues?
          </p>
          <Link
            href="/contact"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Contact Support →
          </Link>
        </div>
      </main>

      {/* Simple footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 py-6 text-center">
        <p className="text-xs text-gray-400">
          © {new Date().getFullYear()} Aditya Labs. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
